import React, { useState, useEffect, useMemo, useRef, FC, memo } from "react";
import { GoogleMap, LoadScript } from "@react-google-maps/api";
import { useQuery } from "@tanstack/react-query";
import {
  findDriverByOrderStatusApi,
  getCurrentLocationDriverOnlineApi,
  getDriverOnlineApi,
} from "../../api/user";
import { filterCarServiceApi } from "../../api/car-service";
import { ICarService } from "../../interface/car-service";
import { getSrcImage } from "../../utils/utils";
import { computeBearing } from "../../utils/geo";
import VehicleMarker from "../../component/vehicle-marker";
import moment from "moment";
import { GOOGLE_MAPS_KEY } from "../../config";

interface IMarker {
  id: string;
  picture: string;
  lat: number;
  lng: number;
  topView?: string;
  heading: number;
}

const Marker: FC<
  google.maps.MarkerOptions & {
    picture: string;
  }
> = memo(({ icon, title, picture, ...options }) => {
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);

  useEffect(() => {
    if (!marker && options.map) {
      const newMarker = new google.maps.Marker();
      newMarker.setMap(options.map);
      setMarker(newMarker);
    }
    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
  }, [marker, options.map]);

  useEffect(() => {
    if (marker && picture) {
      const img = new Image();
      img.src = picture;
      img.alt = "";
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 40;
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, 0, 0, size, size);
          ctx.restore();
          marker.setOptions({
            ...options,
            icon: {
              url: canvas.toDataURL(),
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(size / 2, size / 2),
            },
          });
        }
      };
    }
  }, [marker, options, picture]);

  return null;
});

Marker.displayName = "Marker";

const SendingMapComponent = () => {
  const [center, setCenter] = useState({ lat: 13.7563, lng: 100.5018 });
  const [filter, setFilter] = useState<{
    licensePlate: string;
    status: string;
  }>({ licensePlate: "", status: "" });
  const mapRef = useRef<google.maps.Map>();
  const [carTypeMap, setCarTypeMap] = useState<{ [key: string]: ICarService }>({});
  const prevPositionsRef = useRef<Map<string, { lat: number; lng: number }>>(new Map());
  const [headings, setHeadings] = useState<{ [key: string]: number }>({});
  const { data } = useQuery({
    queryKey: ["getDriverOnlineAndLocation"],
    queryFn: async () => {
      return await Promise.all([
        getDriverOnlineApi(),
        getCurrentLocationDriverOnlineApi(),
      ]);
    },
    refetchInterval: 5000,
  });
  const { data: driverByOrderStatus } = useQuery({
    queryKey: ["getDriverOnlineByOrderStatus", filter.status],
    queryFn: async () => {
      if (filter.status !== "")
        return await findDriverByOrderStatusApi(filter.status);
      return null;
    },
  });

  // Car types (for topViewPicture) - fetched once on mount.
  useEffect(() => {
    const loadCarTypes = async () => {
      try {
        const { data } = await filterCarServiceApi({ status: 1 });
        const map = (data ?? []).reduce(
          (a: { [key: string]: ICarService }, b: ICarService) => ({
            ...a,
            [b.id]: b,
          }),
          {} as { [key: string]: ICarService }
        );
        setCarTypeMap(map);
      } catch (e) {
        // fall back to photo markers silently
      }
    };
    loadCarTypes();
  }, []);

  // Track heading per driver from consecutive location updates. Also prunes
  // drivers that dropped offline so these maps don't grow unbounded over a
  // long-running session.
  useEffect(() => {
    if (data && data[1].data) {
      const currentIds = new Set(data[1].data.map((loc) => loc.id));

      const prevPositions = prevPositionsRef.current;
      Array.from(prevPositions.keys()).forEach((id) => {
        if (!currentIds.has(id)) prevPositions.delete(id);
      });

      setHeadings((prev) => {
        const next: { [key: string]: number } = {};
        data[1].data.forEach((loc) => {
          const prevPos = prevPositions.get(loc.id);
          if (prevPos && (prevPos.lat !== loc.lat || prevPos.lng !== loc.lng)) {
            next[loc.id] = computeBearing(prevPos, loc);
          } else {
            next[loc.id] = prev[loc.id] ?? 0;
          }
          prevPositions.set(loc.id, { lat: loc.lat, lng: loc.lng });
        });
        return next;
      });
    }
    // eslint-disable-next-line
  }, [data]);

  const markers = useMemo<IMarker[]>(() => {
    if (data && data[1].data) {
      const drivers = data[1].data.reduce((a, b) => {
        const driver = data[0]?.data?.find((e) => e.id === b.id);
        if (driver) {
          if (
            filter.licensePlate !== "" &&
            !driver.licensePlate
              .toUpperCase()
              .includes(filter.licensePlate.toUpperCase())
          )
            return a;
          const vehicleType =
            b.vehicleType && b.vehicleType !== "000000000000000000000000"
              ? b.vehicleType
              : driver?.vehicleType;
          const topView = vehicleType
            ? carTypeMap[vehicleType]?.topViewPicture
            : undefined;
          return {
            ...a,
            [b.id]: {
              id: b.id,
              lat: b.lat,
              lng: b.lng,
              picture: driver.picture,
              topView,
              heading: headings[b.id] ?? 0,
            },
          };
        }
        return a;
      }, {} as { [key: string]: IMarker });
      let filteredDriver: IMarker[] = Object.values(drivers);
      if (filter.status !== "" && driverByOrderStatus) {
        filteredDriver = (driverByOrderStatus.data ?? []).reduce((a, b) => {
          if (drivers[b.id]) return [...a, drivers[b.id]];
          return a;
        }, [] as IMarker[]);
      }
      return filteredDriver ?? [];
    }
    return [];
  }, [data, filter, driverByOrderStatus, carTypeMap, headings]);

  const mapStyles = {
    height: "400px",
    width: "100%",
  };

  const onDragEnd = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      if (center) {
        setCenter({
          lat: center.lat(),
          lng: center.lng(),
        });
      }
    }
  };

  return (
    <>
      <div className="row mb-2">
        <div className="col-lg">
          <h4 className="mt-2">
            {`${moment().format("DD")} ${moment().format(
              "MMMM"
            )} ${moment().format("YYYY")}`}
          </h4>
        </div>
        <div className="col-lg ml-auto">
          <input
            type="text"
            className="form-control"
            id="car_number_filter"
            placeholder="ค้นหาตามทะเบียนรถ"
            onChange={(e) =>
              setFilter((prev) => ({
                ...prev,
                licensePlate: e.target.value ?? "",
              }))
            }
          />
        </div>
        <div className="col-lg">
          <select
            className="form-control"
            onChange={(e) =>
              setFilter((prev) => ({ ...prev, status: e.target.value ?? "" }))
            }
          >
            <option value="">สถานะทั้งหมด</option>
            <option value="2">กำลังดำเนินการ</option>
            <option value="3">กำลังจัดส่ง</option>
          </select>
        </div>
      </div>

      <div className="row mb-2">
        <div className="col-12">
          <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY}>
            <GoogleMap
              mapContainerStyle={mapStyles}
              zoom={15}
              center={center}
              options={{
                disableDefaultUI: true, // Hides all default UI controls
                zoomControl: true,
                mapTypeControl: false,
                scaleControl: true,
                streetViewControl: false,
                rotateControl: true,
                fullscreenControl: true,
              }}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              onDragEnd={onDragEnd}
            >
              {markers.map((e) =>
                e.topView ? (
                  <VehicleMarker
                    key={e.id}
                    position={{ lat: e.lat, lng: e.lng }}
                    imageUrl={getSrcImage(e.topView)}
                    heading={e.heading}
                  />
                ) : (
                  <Marker
                    key={e.id}
                    position={{ lat: e.lat, lng: e.lng }}
                    picture={getSrcImage(e.picture)}
                    map={mapRef.current}
                  />
                )
              )}
            </GoogleMap>
          </LoadScript>
        </div>
      </div>
    </>
  );
};

export default SendingMapComponent;
